// Framework content types - the questionnaire structure (pillars + questions).
// The concrete content lives in ../framework/framework.v1.json, validated by
// ../framework/schema.ts.

export type Risk = 'High' | 'Medium' | 'Low';
export type Category = 'Core' | 'Lens';

/**
 * A pillar id is a free-form short code (e.g. "OPS", "SEC"). Each WAR/framework
 * declares its own pillar set, so ids are not a fixed global enum — different
 * reviews (Databricks, Applied AI, ...) define different pillars. Core vs lens
 * is determined by each pillar's `category`, never by its id.
 */
export type PillarId = string;

/** The Databricks WAR's core pillars - its default selection. Display order. */
export const CORE_PILLAR_IDS = ['OPS', 'SEC', 'REL', 'PERF', 'COST', 'GOV', 'INT'] as const;
/** The Databricks WAR's optional lenses - assessed only when selected. */
export const LENS_PILLAR_IDS = ['AI', 'APP'] as const;

export type CorePillarId = (typeof CORE_PILLAR_IDS)[number];
export type LensPillarId = (typeof LENS_PILLAR_IDS)[number];

export interface Question {
  /** e.g. "OPS-01" (Well-Architected) or "DEF-S01"/"DEF-T01" (strategic/tactical). */
  id: string;
  pillarId: PillarId;
  category: Category;
  question: string;
  whatGoodLooksLike: string;
  risk: Risk;
  /** Why it matters. Optional — some frameworks omit a per-question rationale. */
  intent?: string;
  /** Action-oriented, gap-closing next step (imperative). Optional; the report
   *  falls back to {@link whatGoodLooksLike} when absent. */
  remediation?: string;
  /** External reference or parent-mechanism trace. Optional. */
  reference?: string;
}

export interface Pillar {
  id: PillarId;
  name: string;
  category: Category;
  description: string;
}

/**
 * Per-framework branding woven into the report and emails, so each assessment
 * keeps its own voice (e.g. the Databricks WAR's "Databricks reference" labels
 * and Databricks-flavored CTAs) without hardcoding it into the shared shell.
 */
export interface FrameworkBranding {
  /** Vendor/technology noun woven into report CTA copy, e.g. "Databricks". Optional. */
  vendor?: string;
  /** Label for a question's reference link, e.g. "Databricks reference". */
  referenceLabel?: string;
}

/**
 * Per-framework vocabulary for the units being assessed. `Category` ('Core' |
 * 'Lens') is the structural backbone; these are the human-facing words rendered
 * around it (e.g. the Databricks WAR's "pillars"/"lenses"). A framework may
 * override any subset; unspecified terms derive from `unit` or fall back to the
 * Well-Architected defaults. See {@link termsFor}.
 */
export interface FrameworkTerms {
  /** Singular unit name, e.g. "pillar". */
  unit: string;
  /** Plural unit name, e.g. "pillars" (defaults to `${unit}s`). */
  unitPlural: string;
  /** Section label for Core-category items, e.g. "Core pillars". */
  coreGroupLabel: string;
  /** Section label for the optional Lens-category items, e.g. "Optional lenses". */
  lensGroupLabel: string;
  /** Per-item eyebrow for a Core item, e.g. "Core pillar". */
  coreItemLabel: string;
  /** Per-item eyebrow for a Lens item, e.g. "Lens". */
  lensItemLabel: string;
}

export interface FrameworkDefinition {
  /** Stable WAR slug, used in the URL (/:focus) and storage key. e.g. "databricks". */
  focus: string;
  /** Display name of the review, e.g. "Databricks Well-Architected Review". */
  title: string;
  /** One-line description shown on the chooser and landing. */
  tagline: string;
  /** Optional report/email branding; omit for a generic, vendor-neutral look. */
  branding?: FrameworkBranding;
  /** Optional vocabulary for the assessed units; omit for Well-Architected defaults. */
  terms?: Partial<FrameworkTerms>;
  pillars: Pillar[];
  questions: Question[];
}

/**
 * Resolve a framework's effective vocabulary, deriving unspecified terms from
 * `unit` and filling Well-Architected defaults so a framework that declares no
 * `terms` reads exactly as the Databricks WAR does today.
 */
export function termsFor(fw: FrameworkDefinition): FrameworkTerms {
  const t = fw.terms ?? {};
  const unit = t.unit?.trim() || 'pillar';
  const unitPlural = t.unitPlural?.trim() || `${unit}s`;
  return {
    unit,
    unitPlural,
    coreGroupLabel: t.coreGroupLabel?.trim() || `Core ${unitPlural}`,
    lensGroupLabel: t.lensGroupLabel?.trim() || 'Optional lenses',
    coreItemLabel: t.coreItemLabel?.trim() || `Core ${unit}`,
    lensItemLabel: t.lensItemLabel?.trim() || 'Lens',
  };
}

/**
 * Resolve a framework's effective branding, filling generic fallbacks so a
 * framework that declares no `branding` still reads sensibly.
 */
export function brandingFor(fw: FrameworkDefinition): { vendor: string; referenceLabel: string } {
  const vendor = fw.branding?.vendor?.trim() ?? '';
  return {
    vendor,
    referenceLabel: fw.branding?.referenceLabel?.trim() || (vendor ? `${vendor} reference` : 'Reference'),
  };
}

/** The default selection for a framework: all of its Core pillars (lenses off). */
export function defaultSelectionFor(framework: FrameworkDefinition): PillarId[] {
  return framework.pillars.filter((p) => p.category === 'Core').map((p) => p.id);
}
